import { useRef, useState, useEffect } from 'react'

export interface UseAutoRefreshOptions {
  baseInterval: number    // Starting poll interval in ms (e.g., 5000, 30000)
  maxInterval: number     // Maximum backoff interval in ms (e.g., 60000, 300000)
  enabled?: boolean       // Whether polling is active (default: true)
}

export interface UseAutoRefreshReturn {
  isBackingOff: boolean       // True when current interval > baseInterval
  currentInterval: number     // Current polling interval in ms
}

export function useAutoRefresh(
  fetchFn: () => Promise<void>,
  options: UseAutoRefreshOptions
): UseAutoRefreshReturn {
  const { baseInterval, maxInterval, enabled = true } = options

  // Store fetchFn in a ref to avoid stale closures and prevent
  // the polling cycle from restarting when the caller recreates fetchFn
  const fetchFnRef = useRef(fetchFn)
  fetchFnRef.current = fetchFn

  // Expose isBackingOff and currentInterval via useState for UI consumption
  const [displayInterval, setDisplayInterval] = useState(baseInterval)

  useEffect(() => {
    if (!enabled) {
      setDisplayInterval(baseInterval)
      return
    }

    // Per-invocation cancellation flag. This avoids the race condition where
    // a shared ref (mountedRef) gets set back to true by a new effect run
    // while an in-flight fetch from the previous invocation is still pending.
    let active = true
    let currentInterval = baseInterval
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    function schedulePoll() {
      timeoutId = setTimeout(async () => {
        try {
          await fetchFnRef.current()
          // On success: reset to base interval
          currentInterval = baseInterval
        } catch {
          // On error: double interval up to max
          currentInterval = Math.min(currentInterval * 2, maxInterval)
        }

        if (active) {
          setDisplayInterval(currentInterval)
          // Schedule the next poll after this one completes (poll-after-response)
          schedulePoll()
        }
      }, currentInterval)
    }

    // Start polling
    setDisplayInterval(baseInterval)
    schedulePoll()

    return () => {
      active = false
      if (timeoutId !== null) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
    }
  }, [baseInterval, maxInterval, enabled])

  return {
    isBackingOff: displayInterval > baseInterval,
    currentInterval: displayInterval,
  }
}
